
import { createContext, useContext, useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";

interface NetworkContextType {
  isOnline: boolean;
}

const NetworkContext = createContext<NetworkContextType>({
  isOnline: true,
});

export const useNetwork = () => useContext(NetworkContext);

interface NetworkProviderProps {
  children: React.ReactNode;
}

export const NetworkProvider = ({ children }: NetworkProviderProps) => {
  const [isOnline, setIsOnline] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isUploadInProgress, setIsUploadInProgress] = useState(false);
  const { toast } = useToast();

  const handleRefresh = async () => {
    if (isUploadInProgress) {
      return; // Don't refresh if upload is in progress
    }

    setIsRefreshing(true);
    try {
      const status = await Network.getStatus();
      setIsOnline(status.connected);
      if (status.connected) {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error checking network status:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    const setupNetwork = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          const status = await Network.getStatus();
          setIsOnline(status.connected);

          // Listen for network status changes
          const networkListener = await Network.addListener('networkStatusChange', status => {
            // Don't update network status if upload is in progress
            if (!isUploadInProgress) {
              setIsOnline(status.connected);
              if (status.connected) {
                toast({
                  title: "Back Online",
                  description: "Your changes will now be synchronized",
                });
              } else {
                toast({
                  title: "Offline Mode",
                  description: "You can continue working. Changes will sync when you're back online",
                });
              }
            }
          });

          // Listen for file upload events
          window.addEventListener('fileUploadStart', () => {
            setIsUploadInProgress(true);
          });

          window.addEventListener('fileUploadEnd', () => {
            setIsUploadInProgress(false);
          });

          return () => {
            networkListener.remove();
            window.removeEventListener('fileUploadStart', () => setIsUploadInProgress(true));
            window.removeEventListener('fileUploadEnd', () => setIsUploadInProgress(false));
          };
        } catch (error) {
          console.error('Error setting up network listener:', error);
        }
      }
    };

    setupNetwork();
  }, [isUploadInProgress]);

  // Prevent network status changes during file uploads
  useEffect(() => {
    if (isUploadInProgress) {
      setIsOnline(true); // Force online state during uploads
    }
  }, [isUploadInProgress]);

  return (
    <NetworkContext.Provider value={{ isOnline }}>
      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 bg-[#ea384c] text-white py-1 px-4 text-sm text-center z-50 flex items-center justify-center space-x-2">
          <span>You're offline</span>
          <Button 
            variant="secondary" 
            size="sm" 
            className="ml-2 !h-6 !py-0 !bg-white !text-[#ea384c] hover:!bg-gray-100"
            onClick={handleRefresh}
            disabled={isRefreshing || isUploadInProgress}
          >
            <RefreshCw className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      )}
      <div className={`${!isOnline ? 'mt-8' : ''}`}>
        {children}
      </div>
    </NetworkContext.Provider>
  );
};

export default NetworkProvider;
